/**
 * The UK map with one pin at the lot, in the gallery's right-hand column, and
 * an "Open in Maps" link. Zoom 6 shows the whole country, as the original did.
 */
import { useEffect, useRef, useState } from 'react'
import { IconExternalLink, IconMapPin } from '@tabler/icons-react'
import type { PropertyRecord } from '../../api/types'
import { isDarkTheme, loadGoogleMaps, themeToken } from '../../services/googleMaps'

export function LotMap({ latitude, longitude, address, coordinateSource }: { latitude: number | null; longitude: number | null; address: string; coordinateSource?: PropertyRecord['coordinateSource'] }) {
  const node = useRef<HTMLDivElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const located = latitude !== null && longitude !== null
  const locationLabel = coordinateSource === 'postcode_centroid' ? 'Postcode centre · approximate' : coordinateSource === 'provided' ? 'Source pin · precision unknown' : coordinateSource === 'manual' ? 'Manually selected pin' : coordinateSource === 'os_open_uprn' ? 'UPRN-linked location' : 'Map · precision unknown'
  const query = located ? `${latitude},${longitude}` : address
  const openUrl = `https://www.google.com/maps/search/?${new URLSearchParams({ api: '1', query })}`

  useEffect(() => {
    if (!node.current || !located) return
    const target = node.current
    let disposed = false
    let pin: google.maps.marker.AdvancedMarkerElement | null = null
    void loadGoogleMaps().then(({ maps, marker, mapId }) => {
      if (disposed) return
      const position = { lat: latitude, lng: longitude }
      const map = new maps.Map(target, { center: position, zoom: 6, mapId, colorScheme: isDarkTheme() ? 'DARK' : 'LIGHT', backgroundColor: themeToken('--muted'), disableDefaultUI: true, gestureHandling: 'cooperative', clickableIcons: false })
      const content = document.createElement('span'); content.className = 'map-pin is-private'; const glyph = document.createElement('span'); content.append(glyph)
      pin = new marker.AdvancedMarkerElement({ map, position, content, title: address })
    }).catch((e: unknown) => { if (!disposed) setError(e instanceof Error ? e.message : 'Google Maps could not be loaded') })
    return () => { disposed = true; if (pin) pin.map = null; target.replaceChildren() }
  }, [latitude, longitude, located, address])

  return (
    <>
      {located ? <div ref={node} className="map-canvas" aria-label={`Map of ${address}: ${locationLabel}`} /> : null}
      {!located || error ? <div className="map-empty"><IconMapPin className="ic" aria-hidden="true" />&nbsp;{error ?? 'No coordinates for this lot'}</div> : null}
      <span className="badge neutral tag">{located ? locationLabel : 'Map'}</span>
      <a className="btn is-small map-open" href={openUrl} target="_blank" rel="noreferrer"><IconExternalLink className="ic-sm" />Open in Maps</a>
    </>
  )
}
